import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import TypingEffect from "./TypingEffect";
// import tw from "./images/typewriter.gif"

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Line, Pie, Bar } from "react-chartjs-2";
import "./OSVisitGraph.css";

// Register required components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

const OSVisitGraph = () => {
  const [dailyVisits, setDailyVisits] = useState([]);
  const [osData, setOsData] = useState([]);
  const [userGroupData, setUserGroupData] = useState([]);
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const currentRef = sectionRef.current; // Store the current ref value
  
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.5 }
    );
  
    if (currentRef) {
      observer.observe(currentRef);
    }
  
    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, []);
  
  useEffect(() => {
    if (isVisible) {
      axios
        .get("/api/get-daily-visits/")
        .then((response) => setDailyVisits(response.data))
        .catch((error) =>
          console.error("Error fetching daily visits:", error)
        );

      axios
        .get("/api/get-os-visits/")
        .then((response) => setOsData(response.data))
        .catch((error) => console.error("Error fetching OS data:", error));

      axios
        .get("/api/get-user-groups/")
        .then((response) => setUserGroupData(response.data))
        .catch((error) =>
          console.error("Error fetching user group data:", error)
        );
    }
  }, [isVisible]);

  const animationOptions = {
    duration: 3000,
    easing: "easeInOutQuart",
  };

  const lineChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    animation: animationOptions,
    plugins: {
      legend: {
        position: "top",
      },
      title: {
        display: true,
        text: "Number of Website Visits",
      },
    },
  };

  const pieChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    animation: animationOptions,
    plugins: {
      legend: {
        position: "top",
      },
      title: {
        display: true,
        text: "Visitor OS Count",
      },
    },
  };

  const barChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    animation: {
      duration: 5000,
      easing: "easeOutBounce",
    },
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: true,
        text: "Registered User Group",
      },
    },
  };

  // Chart data
  const lineData = {
    labels: dailyVisits.map((item) => item.date),
    datasets: [
      {
        label: "Daily Visits",
        data: dailyVisits.map((item) => item.visit_count),
        borderColor: "blue",
        backgroundColor: "lightblue",
        fill: false,
      },
    ],
  };

  const pieData = {
    labels: osData.map((item) => item.os_name),
    datasets: [
      {
        data: osData.map((item) => item.visit_count),
        backgroundColor: ["red", "blue", "green", "yellow", "purple", "orange"],
      },
    ],
  };

  const barData = {
    labels: Object.keys(userGroupData),
    datasets: [
      {
        label: "User Count by Group",
        data: Object.values(userGroupData),
        backgroundColor: ["cyan", "magenta", "lime", "orange"],
      },
    ],
  };
  return (
    <div ref={sectionRef} className="section num4" id="os-visit-graph">
      <h2 className="Websection-title">Website Data</h2>

      {/* Top Card */}
      <div className="Webtop-card">
        {isVisible && (
          <>
            <div className="line-graph-card">
              <div className="chart-card-header">
                <i className="fas fa-thumbtack"></i> {/* Pin Tack Icon */}
              </div>
              <Line data={lineData} options={lineChartOptions} />
            </div>
            <div className="pie-graph-card">
              <div className="chart-card-header">
                <i className="fas fa-thumbtack"></i> {/* Pin Tack Icon */}
              </div>
              <Pie data={pieData} options={pieChartOptions} />
            </div>
          </>
        )}
      </div>

      {/* Bottom Card */}
      <div className="Webbottom-card">
        {isVisible && (
          <>
            <div className="topic-card">
              <div className="chart-card-header">
                <i className="fas fa-paperclip"></i> {/* Paper Clip Icon */}
              </div>
              <h3>
                <TypingEffect text="About the Data" speed={100} pause={2000} /> 
              </h3>
              <p>
                The data displayed in these charts is collected using APIs that
                track daily visits, operating systems, and user groups. The
                graphs are created dynamically using Chart.js, a powerful
                JavaScript library for visualizing data.
              </p>
            </div>
            <div className="bar-chart-card">
              {/* <div className="chart-card-header">
                <i className="fas fa-thumbtack"></i>
              </div> */}
              <Bar data={barData} options={barChartOptions} />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default OSVisitGraph;
